import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { ApiError, asList } from '../lib/api';
import { DataTable, type Column } from '../components/DataTable';
import { hrApi, type Employee, type Named, type LeaveType, type LeaveRequest, type Holiday } from '../lib/hrApi';
import { ModuleStrip } from '../components/ModuleStrip';

/// HR administration (tenant-admin): employee records, the leave-approval inbox, and config
/// (departments, designations, leave types, holidays). Payroll is deliberately not here.
export function HrPage() {
  const [tab, setTab] = useState<'employees' | 'leave' | 'config'>('employees');
  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>People</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>HR</h1>
        <p style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 4 }}>Employee records and leave. Staff logins &amp; roles stay in Administration → Staff &amp; access.</p>
      </div>
      <ModuleStrip module="hr" />

      <div className="tab-strip" style={{ gap: 4, marginBottom: 16 }}>
        <TabBtn active={tab === 'employees'} onClick={() => setTab('employees')}>Employees</TabBtn>
        <TabBtn active={tab === 'leave'} onClick={() => setTab('leave')}>Leave inbox</TabBtn>
        <TabBtn active={tab === 'config'} onClick={() => setTab('config')}>Config</TabBtn>
      </div>
      {tab === 'employees' && <EmployeesTab />}
      {tab === 'leave' && <LeaveInboxTab />}
      {tab === 'config' && <ConfigTab />}
    </div>
  );
}

function EmployeesTab() {
  const [rows, setRows] = useState<Employee[]>([]);
  const [depts, setDepts] = useState<Named[]>([]);
  const [desigs, setDesigs] = useState<Named[]>([]);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    hrApi.listEmployees().then((r) => setRows(asList(r))).catch(() => setRows([]));
    hrApi.listDepartments().then((r) => setDepts(asList(r))).catch(() => setDepts([]));
    hrApi.listDesignations().then((r) => setDesigs(asList(r))).catch(() => setDesigs([]));
  }, []);
  useEffect(load, [load]);

  const deptName = (id: string | null) => depts.find((d) => d.id === id)?.name ?? '—';
  const desigName = (id: string | null) => desigs.find((d) => d.id === id)?.name ?? '—';

  /**
   * Columns.
   *
   * ⚠ Inside the component because department and designation are resolved through lookups held in
   * state — a module-scope definition could not see them.
   */
  const columns: Column<typeof rows[number]>[] = [
    {
      id: 'code', header: 'Code', sortable: true, hideSm: true,
      value: (e) => e.employeeCode,
      cell: (e) => <span className="mono" style={{ fontSize: 12 }}>{e.employeeCode}</span>,
    },
    {
      id: 'name', header: 'Name', sortable: true, primary: true,
      // Code folded into the search value so typing it finds the person — an HR clerk holding a
      // payslip has the code, not the spelling.
      value: (e) => `${e.fullName} ${e.employeeCode}`,
      cell: (e) => <strong>{e.fullName}</strong>,
    },
    {
      id: 'dept', header: 'Department', sortable: true, hideMd: true,
      value: (e) => deptName(e.departmentId),
      cell: (e) => deptName(e.departmentId),
    },
    {
      id: 'desig', header: 'Designation', sortable: true,
      value: (e) => desigName(e.designationId),
      cell: (e) => desigName(e.designationId),
    },
    {
      id: 'type', header: 'Type', sortable: true, hideMd: true,
      value: (e) => e.employmentType,
      cell: (e) => e.employmentType,
    },
    {
      id: 'status', header: 'Status', sortable: true,
      value: (e) => e.status,
      cell: (e) => (
        <span className={`pill ${e.status === 'Active' ? 'pill-ok' : 'pill-muted'}`} style={{ fontSize: 11 }}>
          {e.status}
        </span>
      ),
    },
    {
      id: 'login', header: 'Login', sortable: true, hideSm: true,
      // ⚠ Sorts so "no login" groups together — that is the actionable set. Someone auditing access
      // wants the people who cannot sign in, not an alphabetical mix.
      value: (e) => (e.userId ? 'linked' : 'no login'),
      cell: (e) => e.userId
        ? <span className="pill pill-muted" style={{ fontSize: 11 }}>linked</span>
        : <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>no login</span>,
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', marginBottom: 12 }}>
        <button className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={() => setAdding(true)}>+ Add employee</button>
      </div>
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(e) => e.id}

        search
        searchPlaceholder="Search by name, code, or department…"

        // Client-side: a school's staff list is tens, not thousands, and the whole set is already
        // loaded to resolve department and designation names.
        pageSize={25}

        empty={{ title: 'No employees yet', body: 'Add your first staff member to get started.' }}
      />

      {adding && <EmployeeDialog depts={depts} desigs={desigs} onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
    </div>
  );
}

function EmployeeDialog({ depts, desigs, onClose, onDone }: { depts: Named[]; desigs: Named[]; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ employeeCode: '', fullName: '', joiningDate: '', departmentId: '', designationId: '', employmentType: 'Permanent', email: '', mobile: '', qualifications: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!f.employeeCode.trim() || !f.fullName.trim() || !f.joiningDate) return;
    setBusy(true); setError(null);
    try {
      await hrApi.createEmployee({
        employeeCode: f.employeeCode.trim(), fullName: f.fullName.trim(), joiningDate: f.joiningDate,
        userId: null, departmentId: f.departmentId || null, designationId: f.designationId || null,
        employmentType: f.employmentType, email: f.email.trim() || null, mobile: f.mobile.trim() || null,
        qualifications: f.qualifications.trim() || null,
      });
      onDone();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ padding: 24, width: 480, maxWidth: '92vw', maxHeight: '86vh', overflowY: 'auto' }}>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 14 }}>New employee</h2>
        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}><label className="label">Employee code</label><input className="input mono" value={f.employeeCode} onChange={(e) => setF({ ...f, employeeCode: e.target.value })} placeholder="EMP001" /></div>
            <div style={{ flex: 1 }}><label className="label">Joining date</label><input className="input" type="date" value={f.joiningDate} onChange={(e) => setF({ ...f, joiningDate: e.target.value })} /></div>
          </div>
          <div><label className="label">Full name</label><input className="input" value={f.fullName} onChange={(e) => setF({ ...f, fullName: e.target.value })} /></div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}><label className="label">Department</label>
              <select className="input" value={f.departmentId} onChange={(e) => setF({ ...f, departmentId: e.target.value })}>
                <option value="">—</option>{depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}><label className="label">Designation</label>
              <select className="input" value={f.designationId} onChange={(e) => setF({ ...f, designationId: e.target.value })}>
                <option value="">—</option>{desigs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          </div>
          <div><label className="label">Employment type</label>
            <select className="input" value={f.employmentType} onChange={(e) => setF({ ...f, employmentType: e.target.value })}>
              <option>Permanent</option><option>Contract</option><option>Visiting</option><option>PartTime</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}><label className="label">Email</label><input className="input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
            <div style={{ flex: 1 }}><label className="label">Mobile</label><input className="input" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} /></div>
          </div>
          <div><label className="label">Qualifications</label><input className="input" value={f.qualifications} onChange={(e) => setF({ ...f, qualifications: e.target.value })} placeholder="M.Tech, Ph.D…" /></div>
        </div>
        <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 10 }}>A login isn't created here — invite them in Administration → Staff &amp; access, then link the account.</p>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-accent" disabled={busy} onClick={save} style={{ marginLeft: 'auto' }}>{busy ? 'Saving…' : 'Add employee'}</button>
        </div>
      </div>
    </div>
  );
}

function LeaveInboxTab() {
  const [rows, setRows] = useState<LeaveRequest[] | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [types, setTypes] = useState<LeaveType[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    hrApi.pendingLeave().then((r) => setRows(asList(r))).catch(() => setRows([]));
    hrApi.listEmployees().then((r) => setEmployees(asList(r))).catch(() => {});
    hrApi.listLeaveTypes().then((r) => setTypes(asList(r))).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const who = (id: string) => employees.find((e) => e.id === id)?.fullName ?? '—';
  const typeName = (id: string) => types.find((t) => t.id === id)?.name ?? '—';

  const review = async (id: string, approve: boolean) => {
    setError(null);
    try { await hrApi.reviewLeave(id, approve); load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not review.'); }
  };

  if (!rows) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  return (
    <div style={{ maxWidth: 640 }}>
      {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}
      {rows.length === 0 ? <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>No leave awaiting approval.</div> :
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((r) => (
            <div key={r.id} className="card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                <strong>{who(r.employeeId)}</strong>
                <span className="pill pill-muted" style={{ fontSize: 11 }}>{typeName(r.leaveTypeId)}</span>
                <span style={{ fontSize: 12, color: 'var(--ink-2)', marginLeft: 'auto' }}>{r.days} day(s)</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 6 }}>{r.fromDate} → {r.toDate}{r.reason ? ` · ${r.reason}` : ''}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="btn btn-accent" onClick={() => review(r.id, true)}>Approve</button>
                <button className="btn" onClick={() => review(r.id, false)}>Reject</button>
              </div>
            </div>
          ))}
        </div>}
      <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 12 }}>Approving debits the employee's balance in the same step — if they don't have the days, the approval is refused.</p>
    </div>
  );
}

function ConfigTab() {
  const [depts, setDepts] = useState<Named[]>([]);
  const [desigs, setDesigs] = useState<Named[]>([]);
  const [types, setTypes] = useState<LeaveType[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [dName, setDName] = useState(''); const [gName, setGName] = useState('');
  const [t, setT] = useState({ name: '', quota: '12', carry: false, maxCarry: '0', paid: true });
  const [h, setH] = useState({ date: '', name: '' });

  const load = useCallback(() => {
    hrApi.listDepartments().then((r) => setDepts(asList(r))).catch(() => {});
    hrApi.listDesignations().then((r) => setDesigs(asList(r))).catch(() => {});
    hrApi.listLeaveTypes().then((r) => setTypes(asList(r))).catch(() => {});
    hrApi.listHolidays().then((r) => setHolidays(asList(r))).catch(() => {});
  }, []);
  useEffect(load, [load]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, alignItems: 'start' }}>
      <Panel title="Departments">
        <Row>
          <input className="input" value={dName} onChange={(e) => setDName(e.target.value)} placeholder="Computer Science" />
          <button className="btn btn-accent" disabled={!dName.trim()} onClick={async () => { await hrApi.createDepartment(dName.trim()); setDName(''); load(); }}>Add</button>
        </Row>
        {depts.map((d) => <Item key={d.id}>{d.name}</Item>)}
      </Panel>

      <Panel title="Designations">
        <Row>
          <input className="input" value={gName} onChange={(e) => setGName(e.target.value)} placeholder="Assistant Professor" />
          <button className="btn btn-accent" disabled={!gName.trim()} onClick={async () => { await hrApi.createDesignation(gName.trim()); setGName(''); load(); }}>Add</button>
        </Row>
        {desigs.map((d) => <Item key={d.id}>{d.name}</Item>)}
      </Panel>

      <Panel title="Leave types">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
          <input className="input" value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} placeholder="Casual leave" />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input className="input" type="number" value={t.quota} onChange={(e) => setT({ ...t, quota: e.target.value })} style={{ width: 80 }} />
            <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>days/yr</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, marginLeft: 'auto' }}>
              <input type="checkbox" checked={t.carry} onChange={(e) => setT({ ...t, carry: e.target.checked })} /> carry fwd
            </label>
          </div>
          {/* ⚠ THE FLAG PAYROLL READS. A day of an unpaid type is deducted from that month's
              salary; a paid one costs the teacher nothing. Ticked by default, because that is what
              every leave type in this product already was. */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
            <input type="checkbox" checked={t.paid} onChange={(e) => setT({ ...t, paid: e.target.checked })} />
            <span>Paid leave</span>
            <span style={{ color: 'var(--ink-3)' }}>
              {t.paid ? '— costs the teacher nothing' : '— deducted from that month\u2019s salary'}
            </span>
          </label>
          <button className="btn btn-accent" disabled={!t.name.trim()} onClick={async () => {
            await hrApi.createLeaveType({ name: t.name.trim(), annualQuota: Number(t.quota), carryForward: t.carry, maxCarryForward: Number(t.maxCarry), isPaid: t.paid });
            setT({ name: '', quota: '12', carry: false, maxCarry: '0', paid: true }); load();
          }}>Add leave type</button>
        </div>
        {types.map((x) => (
          <Item key={x.id}>
            {x.name} <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>· {x.annualQuota}/yr</span>
            {/* ⚠ Shown on every row, because "which of these costs money" is the question payroll
                asks and nobody can answer from a name alone. */}
            {!x.isPaid && (
              <span className="eyebrow" style={{ fontSize: 9, marginLeft: 6, color: 'var(--warn)' }}>unpaid</span>
            )}
          </Item>
        ))}
      </Panel>

      <Panel title="Holidays">
        <Row>
          <input className="input" type="date" value={h.date} onChange={(e) => setH({ ...h, date: e.target.value })} />
          <input className="input" value={h.name} onChange={(e) => setH({ ...h, name: e.target.value })} placeholder="Diwali" />
          <button className="btn btn-accent" disabled={!h.date || !h.name.trim()} onClick={async () => { await hrApi.createHoliday(h.date, h.name.trim()); setH({ date: '', name: '' }); load(); }}>Add</button>
        </Row>
        {holidays.map((x) => <Item key={x.id}>{x.date} — {x.name}</Item>)}
        <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6 }}>Holidays and Sundays are excluded when counting a leave application's days.</p>
      </Panel>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return <div className="card" style={{ padding: 16 }}><div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 10 }}>{title}</div>{children}</div>;
}
function Row({ children }: { children: ReactNode }) {
  return <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>{children}</div>;
}
function Item({ children }: { children: ReactNode }) {
  return <div style={{ padding: '6px 0', borderBottom: '1px solid var(--line-2)', fontSize: 13 }}>{children}</div>;
}
function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return <button onClick={onClick} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px', fontSize: 14, color: active ? 'var(--ink)' : 'var(--ink-3)', fontWeight: active ? 600 : 400, borderBottom: active ? '2px solid var(--accent,#C1272D)' : '2px solid transparent' }}>{children}</button>;
}
